import React, { useEffect, useState } from "react";
import {
	Box,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Button,
	IconButton,
	Typography,
	Chip,
	Tooltip,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
} from "@mui/material";
import { Delete as DeleteIcon, Add as AddIcon } from "@mui/icons-material";
import { useTranslationContext } from "../../../../hooks/translations/translationsProvider";
import { getRoles, createRole, deleteRole } from "../../../../hooks/actions/identityManagement";
import { RoleResponse } from "../../../../models/identity/IdentityManagement";
import {
	bodyCellSx,
	headCellSx,
	outlinedButtonSx,
	primaryButtonSx,
	roleChipSx,
	tableContainerSx,
} from "./identityStyles";

const RolesList: React.FC = () => {
	const { t } = useTranslationContext();
	const [items, setItems] = useState<RoleResponse[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [createOpen, setCreateOpen] = useState(false);
	const [newName, setNewName] = useState("");

	const load = async () => {
		setError(null);
		setLoading(true);
		try {
			setItems(await getRoles());
		} catch (e: any) {
			setError(t("Errore nel caricamento dei ruoli"));
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		load();
	}, []);

	const handleCreate = async () => {
		const name = newName.trim();
		if (!name) return;
		setError(null);
		try {
			const res = await createRole(name);
			if (res?.succeeded) {
				setCreateOpen(false);
				setNewName("");
				await load();
			} else {
				setError(res?.error || t("Operazione non riuscita"));
			}
		} catch (e: any) {
			setError(e?.error || t("Operazione non riuscita"));
		}
	};

	const handleDelete = async (role: RoleResponse) => {
		if (role.isSystemRole) return;
		if (!window.confirm(t("Confermi l'eliminazione del ruolo?"))) return;
		setError(null);
		try {
			const res = await deleteRole(role.id);
			if (res?.succeeded) {
				await load();
			} else {
				setError(res?.error || t("Operazione non riuscita"));
			}
		} catch (e: any) {
			setError(e?.error || t("Operazione non riuscita"));
		}
	};

	return (
		<Box>
			{error && (
				<Typography color="error" sx={{ mb: 2 }}>
					{error}
				</Typography>
			)}

			<Box sx={{ display: "flex", gap: 1, mb: 2 }}>
				<Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)} sx={primaryButtonSx}>
					{t("Nuovo ruolo")}
				</Button>
			</Box>

			<TableContainer component={Paper} sx={tableContainerSx}>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell sx={headCellSx}>{t("Ruolo")}</TableCell>
							<TableCell sx={headCellSx}>{t("Tipo")}</TableCell>
							<TableCell sx={headCellSx} align="right">{t("Azioni")}</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{loading ? (
							<TableRow>
								<TableCell sx={bodyCellSx} colSpan={3}>{t("Caricamento...")}</TableCell>
							</TableRow>
						) : (
							items.map((role) => (
								<TableRow key={role.id} hover>
									<TableCell sx={bodyCellSx}>{role.name}</TableCell>
									<TableCell sx={bodyCellSx}>
										{role.isSystemRole ? (
											<Chip size="small" label={t("Sistema")} sx={roleChipSx} />
										) : (
											<Chip size="small" variant="outlined" label={t("Personalizzato")} sx={roleChipSx} />
										)}
									</TableCell>
									<TableCell sx={bodyCellSx} align="right">
										<Tooltip title={role.isSystemRole ? t("Ruolo di sistema: non eliminabile") : t("Elimina")}>
											<span>
												<IconButton
													size="small"
													color="error"
													disabled={role.isSystemRole}
													onClick={() => handleDelete(role)}
												>
													<DeleteIcon />
												</IconButton>
											</span>
										</Tooltip>
									</TableCell>
								</TableRow>
							))
						)}
					</TableBody>
				</Table>
			</TableContainer>

			<Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="xs">
				<DialogTitle>{t("Nuovo ruolo")}</DialogTitle>
				<DialogContent>
					<TextField
						autoFocus
						fullWidth
						margin="dense"
						label={t("Nome ruolo")}
						value={newName}
						onChange={(e) => setNewName(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === "Enter") handleCreate();
						}}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setCreateOpen(false)} sx={outlinedButtonSx}>{t("Annulla")}</Button>
					<Button variant="contained" onClick={handleCreate} disabled={!newName.trim()} sx={primaryButtonSx}>
						{t("Crea")}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default RolesList;
